import clsx from 'clsx';
import { CheckCircle, XCircle } from 'lucide-react';

import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { Icon } from '@@/Icon';

import { Recommendation } from './types';

export const GOVERNANCE_CHECKS: {
  typeId: string;
  pass: string;
  fail: string;
}[] = [
  {
    typeId: 'offline-environments',
    pass: 'No offline environments',
    fail: 'Offline environments detected',
  },
  {
    typeId: 'agents-need-upgrade',
    pass: 'All agents up to date',
    fail: 'Agents need upgrading',
  },
  {
    typeId: 'unassigned-environments',
    pass: 'All environments assigned to groups',
    fail: 'Unassigned environments',
  },
  {
    typeId: 'ungoverned-groups',
    pass: 'All groups have policies',
    fail: 'Groups without policies',
  },
  {
    typeId: 'unattached-policies',
    pass: 'All policies attached to groups',
    fail: 'Unattached policies',
  },
];

export const GOVERNANCE_CHECK_COUNT = GOVERNANCE_CHECKS.length;

export function computeGovernanceCompletion(recommendations: Recommendation[]) {
  const activeTypeIds = new Set(recommendations.map((r) => r.typeId));
  const failCount = GOVERNANCE_CHECKS.filter((c) =>
    activeTypeIds.has(c.typeId)
  ).length;
  const passCount = GOVERNANCE_CHECK_COUNT - failCount;
  const percent = Math.round((passCount / GOVERNANCE_CHECK_COUNT) * 100);
  return { failCount, passCount, percent };
}

interface Props {
  recommendations: Recommendation[];
  className?: string;
}

export function GovernanceProgressBar({ recommendations, className }: Props) {
  const activeTypeIds = new Set(recommendations.map((r) => r.typeId));
  const { passCount } = computeGovernanceCompletion(recommendations);

  // Sort: passing checks first (green on left), failing checks after
  const sorted = [...GOVERNANCE_CHECKS].sort((a, b) => {
    const aFailing = activeTypeIds.has(a.typeId) ? 1 : 0;
    const bFailing = activeTypeIds.has(b.typeId) ? 1 : 0;
    return aFailing - bFailing;
  });

  return (
    <div
      className={clsx('flex w-full items-center gap-1', className)}
      role="progressbar"
      aria-label="Governance completion"
      aria-valuenow={passCount}
      aria-valuemin={0}
      aria-valuemax={GOVERNANCE_CHECK_COUNT}
    >
      {sorted.map((check) => (
        <GovernanceCheckSegment
          key={check.typeId}
          check={check}
          isFailing={activeTypeIds.has(check.typeId)}
        />
      ))}
    </div>
  );
}

export function GovernanceChecklist({
  recommendations,
}: {
  recommendations: Recommendation[];
}) {
  const activeTypeIds = new Set(recommendations.map((r) => r.typeId));

  const sorted = [...GOVERNANCE_CHECKS].sort((a, b) => {
    const aFailing = activeTypeIds.has(a.typeId) ? 1 : 0;
    const bFailing = activeTypeIds.has(b.typeId) ? 1 : 0;
    return aFailing - bFailing;
  });

  return (
    <ul className="m-0 list-none space-y-1.5 p-0">
      {sorted.map((check) => {
        const isFailing = activeTypeIds.has(check.typeId);
        return (
          <li key={check.typeId} className="flex items-center gap-2 text-sm">
            <Icon
              icon={isFailing ? XCircle : CheckCircle}
              className={clsx(
                'h-4 w-4 flex-shrink-0 th-highcontrast:!text-white',
                isFailing
                  ? '!text-error-7 th-dark:!text-error-5'
                  : '!text-success-7 th-dark:!text-success-5'
              )}
            />
            <span
              className={clsx(
                'th-highcontrast:text-white',
                isFailing
                  ? 'text-gray-8 th-dark:text-white'
                  : 'text-gray-6 th-dark:text-gray-4'
              )}
            >
              {isFailing ? check.fail : check.pass}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function GovernanceCheckSegment({
  check,
  isFailing,
}: {
  check: (typeof GOVERNANCE_CHECKS)[number];
  isFailing: boolean;
}) {
  const tooltip = isFailing ? check.fail : check.pass;

  return (
    <TooltipWithChildren message={tooltip} position="top">
      <div
        className={clsx(
          'h-full flex-1 cursor-default transition-colors duration-300',
          'border border-blue-7/50 th-highcontrast:border-white th-dark:border-blue-8',
          'first:rounded-l-full last:rounded-r-full',
          isFailing
            ? 'bg-success-5/20 th-highcontrast:bg-success-10/30 th-dark:bg-success-10/30'
            : 'bg-success-7/75 th-highcontrast:bg-success-7 th-dark:bg-success-7'
        )}
      />
    </TooltipWithChildren>
  );
}
